import { generateSignal, strategies } from "./strategies";
export function backtestStrategy(symbol, assetType, strategyId, candles, config = { minimumTrades: 20, trainRatio: 0.72 }) {
    const strategy = strategies.find((item) => item.id === strategyId);
    if (!strategy || candles.length < strategy.minimumBars + 10) {
        return { metrics: emptyMetrics(symbol, strategyId, true), trades: [] };
    }
    const trades = [];
    for (let i = strategy.minimumBars; i < candles.length - 5; i += 1) {
        const slice = candles.slice(0, i + 1);
        const signal = generateSignal({ symbol, assetType, strategyId, candles: slice });
        if (!signal)
            continue;
        const forward = candles.slice(i + 1, Math.min(i + 16, candles.length));
        const exit = simulateExit(signal.entry, signal.stopLoss, signal.target1, signal.direction, forward);
        trades.push({
            entryAt: candles[i].t,
            exitAt: exit.timestamp,
            direction: signal.direction,
            entry: signal.entry,
            exit: exit.price,
            stopLoss: signal.stopLoss,
            target: signal.target1,
            rMultiple: exit.rMultiple
        });
        i += 5;
    }
    return {
        metrics: metricsFromTrades(symbol, strategyId, trades, config),
        trades
    };
}
export function metricsFromTrades(symbol, strategyId, trades, config = { minimumTrades: 20, trainRatio: 0.72 }) {
    const wins = trades.filter((trade) => trade.rMultiple > 0);
    const losses = trades.filter((trade) => trade.rMultiple < 0);
    const grossProfit = wins.reduce((sum, trade) => sum + trade.rMultiple, 0);
    const grossLoss = Math.abs(losses.reduce((sum, trade) => sum + trade.rMultiple, 0));
    const expectancy = average(trades.map((trade) => trade.rMultiple));
    const split = Math.max(1, Math.floor(trades.length * config.trainRatio));
    const trainExpectancy = average(trades.slice(0, split).map((trade) => trade.rMultiple));
    const outOfSampleExpectancy = average(trades.slice(split).map((trade) => trade.rMultiple));
    const maxDrawdown = calculateMaxDrawdown(trades.map((trade) => trade.rMultiple));
    const recentPerformance = average(trades.slice(-8).map((trade) => trade.rMultiple));
    const stabilityScore = calculateStability(trades.map((trade) => trade.rMultiple), trainExpectancy, outOfSampleExpectancy);
    return {
        strategyId,
        symbol,
        trades: trades.length,
        winRate: trades.length ? wins.length / trades.length : 0,
        profitFactor: grossLoss === 0 ? (grossProfit > 0 ? 9.99 : 0) : grossProfit / grossLoss,
        expectancy,
        maxDrawdown,
        averageRiskReward: average(trades.map((trade) => Math.abs((trade.target - trade.entry) / (trade.entry - trade.stopLoss)))),
        recentPerformance,
        outOfSampleExpectancy,
        stabilityScore,
        overfitWarning: trainExpectancy > 0.4 && outOfSampleExpectancy < 0,
        tooFewTradesWarning: trades.length < config.minimumTrades
    };
}
function simulateExit(entry, stopLoss, target, direction, forward) {
    const risk = direction === "LONG" ? entry - stopLoss : stopLoss - entry;
    for (const candle of forward) {
        const stopped = direction === "LONG" ? candle.l <= stopLoss : candle.h >= stopLoss;
        const targeted = direction === "LONG" ? candle.h >= target : candle.l <= target;
        if (stopped || targeted) {
            const price = stopped ? stopLoss : target;
            return { timestamp: candle.t, price, rMultiple: stopped ? -1 : Math.abs((target - entry) / risk) };
        }
    }
    const last = forward.at(-1) ?? { t: new Date().toISOString(), c: entry };
    const pnl = direction === "LONG" ? last.c - entry : entry - last.c;
    return { timestamp: last.t, price: last.c, rMultiple: pnl / risk };
}
function calculateMaxDrawdown(returns) {
    let equity = 0;
    let peak = 0;
    let maxDrawdown = 0;
    returns.forEach((value) => {
        equity += value;
        peak = Math.max(peak, equity);
        maxDrawdown = Math.min(maxDrawdown, equity - peak);
    });
    return Math.abs(maxDrawdown);
}
function calculateStability(returns, train, outOfSample) {
    if (!returns.length)
        return 0;
    const mean = average(returns);
    const variance = average(returns.map((value) => (value - mean) ** 2));
    const consistency = Math.max(0, 1 - Math.sqrt(variance));
    const oosPenalty = outOfSample < train * 0.25 ? 0.7 : 1;
    return Number(Math.max(0, Math.min(1, consistency * oosPenalty)).toFixed(2));
}
function average(values) {
    if (!values.length)
        return 0;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
}
function emptyMetrics(symbol, strategyId, tooFewTradesWarning) {
    return {
        strategyId,
        symbol,
        trades: 0,
        winRate: 0,
        profitFactor: 0,
        expectancy: 0,
        maxDrawdown: 0,
        averageRiskReward: 0,
        recentPerformance: 0,
        outOfSampleExpectancy: 0,
        stabilityScore: 0,
        overfitWarning: false,
        tooFewTradesWarning
    };
}
